/**
 * Web Audio API ringback tone generator.
 * Produces a caller-side "ringing" tone (similar to phone ringback).
 * Pattern: 1000ms tone → 3000ms silence (repeat)
 * Uses 440Hz + 480Hz dual-tone (North American ringback standard).
 */

let audioContext: AudioContext | null = null;
let oscillator1: OscillatorNode | null = null;
let oscillator2: OscillatorNode | null = null;
let gainNode: GainNode | null = null;
let ringInterval: ReturnType<typeof setInterval> | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

/**
 * Start playing a ringback tone in a repeating pattern:
 * 1000ms tone → 3000ms silence (repeat)
 */
export function startRingback(): void {
  stopRingback();

  const ctx = getAudioContext();
  if (ctx.state === "suspended") {
    ctx.resume();
  }

  gainNode = ctx.createGain();
  gainNode.gain.value = 0;
  gainNode.connect(ctx.destination);

  oscillator1 = ctx.createOscillator();
  oscillator1.type = "sine";
  oscillator1.frequency.value = 440;
  oscillator1.connect(gainNode);
  oscillator1.start();

  oscillator2 = ctx.createOscillator();
  oscillator2.type = "sine";
  oscillator2.frequency.value = 480;
  oscillator2.connect(gainNode);
  oscillator2.start();

  const pattern = [
    { gain: 0.15, duration: 1000 },
    { gain: 0, duration: 3000 },
  ];
  const totalCycle = pattern.reduce((sum, p) => sum + p.duration, 0);

  let elapsed = 0;
  const applyPattern = () => {
    if (!gainNode) return;
    let t = elapsed % totalCycle;
    for (const step of pattern) {
      if (t < step.duration) {
        gainNode.gain.value = step.gain;
        return;
      }
      t -= step.duration;
    }
  };

  applyPattern();
  ringInterval = setInterval(() => {
    elapsed += 50;
    applyPattern();
  }, 50);
}

/**
 * Stop the ringback tone and clean up resources.
 */
export function stopRingback(): void {
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
  const stopOsc = (osc: OscillatorNode | null) => {
    if (osc) {
      try { osc.stop(); osc.disconnect(); } catch { /* already stopped */ }
    }
  };
  stopOsc(oscillator1);
  stopOsc(oscillator2);
  oscillator1 = null;
  oscillator2 = null;
  if (gainNode) {
    gainNode.disconnect();
    gainNode = null;
  }
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
}
